/**
 * Protección de Taps — Guía del administrador (Español)
 */
const content = `# Protección de Taps — Guía del administrador

**Dónde encontrar estos ajustes**: Modal **Ajustes** de cada tarjeta → sección *Protección de Taps*.
**Dónde ver la ejecución en vivo**: **Super Admin → Monitor de salud de Taps** (\`/admin/tap-health\`).

---

## En 30 segundos

La Protección de Taps decide si el tap NFC de un cliente se cuenta o se filtra como sospechoso.

* **Protección de Taps ACTIVADA** — la plataforma aplica los controles antifraude. Solo cuentan los taps "limpios".
* **Protección de Taps DESACTIVADA** — cada tap legítimo se cuenta, sin filtro. Elíjala si desea analíticas sin filtro.

Bajo el interruptor principal hay dos subprotecciones independientes:

| Subprotección | Bloquea | Se aplica a |
|---|---|---|
| **Protección contra duplicados** | Taps repetidos del mismo teléfono en una ventana corta | Todas las tarjetas |
| **Protección de tiempo mínimo** | Taps donde el cliente no se quedó lo suficiente en la página | Tarjetas Landing Page y Review Shield únicamente |

---

## Cómo activar la Protección de Taps

1. Abra **Tarjetas** en la barra lateral.
2. Encuentre la tarjeta y haga clic en **Ajustes**.
3. Baje a la sección **Protección de Taps**.
4. Cambie **Protección de Taps** a **ACTIVADA**.
5. Haga clic en **Guardar**.

Una vez activada, las dos subprotecciones aparecen debajo, **activadas por defecto**.

## Cómo desactivar la Protección de Taps

1. Abra **Tarjetas → Ajustes** para la tarjeta.
2. Cambie el interruptor principal **Protección de Taps** a **DESACTIVADA**.
3. Haga clic en **Guardar**.

Mientras esté desactivada, cada tap legítimo se cuenta. Las reglas de Duplicado y Tiempo mínimo se ignoran.

## Configurar la Protección contra duplicados

Impide que el mismo teléfono se cuente más de una vez dentro de la ventana elegida.

1. Con **Protección de Taps** activada, mire la subsección **Protección contra duplicados**.
2. Actívela (por defecto) o desactívela.
3. Ajuste la **Ventana (segundos)** — el intervalo entre dos taps del mismo dispositivo.
   * \`30 s\` (por defecto) — uso general.
   * \`60 s\` — restaurantes con mostradores de alta rotación donde asociado y cliente pueden tocar juntos.
   * \`15 s\` — kioscos de alto volumen donde casi cada tap es un cliente distinto.
4. Haga clic en **Guardar**.

## Configurar la Protección de tiempo mínimo

Requiere que el cliente permanezca un tiempo mínimo en la Landing Page o el Review Shield antes de contar el tap.

1. Con **Protección de Taps** activada, mire la subsección **Protección de tiempo mínimo**.
2. Actívela (por defecto) o desactívela.
3. Ajuste el **Umbral (segundos)** — el tiempo mínimo.
   * \`10 s\` (por defecto) — la mayoría de los negocios.
   * \`20 s\` — si tiene un video de intro largo y quiere compromiso real.
   * \`5 s\` — contenido corto, flujo minorista rápido.
4. Haga clic en **Guardar**.

> En una tarjeta **Direct Tap** (sin landing page), la Protección de tiempo mínimo se muestra **desactivada** con un aviso ámbar. Es intencional — no se puede activar porque no hay página en la que medir el tiempo. Cambiar la tarjeta al modo Landing Page reactiva el conmutador automáticamente.

---

## Diferencias entre Direct Tap y Landing Page

| Aspecto | Direct Tap | Landing Page |
|---|---|---|
| Lo que ve el cliente al tocar | Redirección inmediata a Google Reviews | Su página con marca primero, luego redirección |
| Protección contra duplicados | Aplica | Aplica |
| Protección de tiempo mínimo | No aplicable | Aplica |
| Rigor antifraude | Solo duplicados | Duplicados + Tiempo |
| Ideal para | Conversión rápida sin fricción | Branding, incentivos, promos multiplataforma |

## Comportamiento de Review Shield

Review Shield es una landing page especial: el cliente debe superar una puerta de satisfacción antes de ser dirigido a Google (satisfecho) o a su formulario privado (insatisfecho). Para la Protección de Taps, Review Shield se trata **igual que Landing Page**:

* Protección contra duplicados se aplica.
* Protección de tiempo mínimo se aplica.
* Ambas se configuran de forma independiente.

---

## Interpretar los resultados del Monitor de salud

La página **Monitor de salud de Taps** (\`/admin/tap-health\`) refleja exactamente lo que la ejecución aplica. Abra el *Diagnóstico en un clic* de una tarjeta para ver:

**Protección de Taps: Activada / Desactivada**
* Activada → al menos una subprotección puede estar en ejecución.
* Desactivada → nada se filtra.

**Protección contra duplicados:**
* *Activada · ventana de 30 s* — el módulo se ejecuta, bloquea repeticiones en 30 s.
* *Desactivada* — el módulo está apagado (el maestro puede seguir activo).
* *Desactivada (Protección de Taps apagada)* — el interruptor maestro está apagado.

**Protección de tiempo mínimo:**
* *Activada · umbral de 10 s* — el módulo se ejecuta para esta tarjeta.
* *No aplicable* — la tarjeta es Direct Tap; no hay página que medir.
* *Desactivada* — el módulo se ha apagado manualmente.

La lista **Supresiones recientes** muestra cada tap rechazado recientemente con su motivo legible ("Tap duplicado" o "Tiempo mínimo no alcanzado"). Si una tarjeta se queja de taps perdidos, mire aquí primero.

## Resolución de taps rechazados

**Síntoma — "Mis taps no cuentan"**

1. Abra \`/admin/tap-health\` y escriba el ID de la tarjeta en el *Diagnóstico en un clic*.
2. Lea el **Veredicto** arriba.

**Veredicto: saludable** — la Protección de Taps está bien. El problema probablemente está en otro sitio.

**Veredicto: silenciosa** — la tarjeta registró cero taps en 7 días. Verifique si la tarjeta se está tocando o si la redirección está rota.

**Veredicto: silenciosa-con-supresiones** — la tarjeta *sí* se está tocando, pero cada tap se rechaza. Mire *Supresiones recientes*:
* Muchos **Tap duplicado** → su ventana es demasiado generosa; redúzcala o desactive temporalmente.
* Muchos **Tiempo mínimo no alcanzado** → los clientes salen antes del umbral; reduzca el umbral o active la redirección automática.

**Veredicto: fallos-de-escritura** — la plataforma no logra persistir los taps. Escale a ingeniería con las marcas de tiempo y tipos de error mostrados.

---

## Preguntas frecuentes

**¿Puedo desactivar una protección y mantener la otra?**
Sí. Duplicados y Tiempo mínimo son totalmente independientes.

**Si desactivo la Protección de Taps, ¿pierdo los datos históricos?**
No. El interruptor solo afecta a los taps futuros. El historial no se toca.

**¿Por qué la Protección de tiempo mínimo aparece gris en algunas tarjetas?**
Porque esas tarjetas están en modo **Direct Tap** sin landing page — no hay página que medir. Cambiar al modo Landing Page la desbloquea.

**¿La Protección de Taps afecta a Google Reviews?**
No. Solo decide si la plataforma *cuenta* un tap en analíticas/incentivos. La redirección a Google ocurre de todas formas.

**¿Cómo pruebo mi nueva configuración?**
1. Toque la tarjeta una vez usted mismo.
2. Abra \`/admin/tap-health\` → Diagnosticar → su card_id.
3. Debería ver el tap en *Taps (24h)*. Toque de nuevo rápidamente — el segundo tap debería contarse (Duplicados off) o aparecer en *Supresiones recientes* como "Tap duplicado" (Duplicados on).
`;

export default content;
